import { Link } from 'react-router-dom';
import clsx from 'clsx';

import SidebarCard from '../ui/SidebarCard';
import Typography from '~/shared/components/Typography';
import LinkIcon from '~/shared/assets/icons/link-gray.svg';
import AutoscalingStatusIcon from '~/shared/assets/icons/autoscaling-status.svg';
import { StorageChangeTag } from '~/shared/components/kpi';
import VectorIcon from '~/shared/assets/icons/vector.svg';
import Button from '~/shared/components/ButtonsV2';
import Tag from '~/shared/components/Tag';
import { formatStorageChangePercent } from '~/shared/utils/formatNumber';

import {
  useExplorationActions,
  useExplorationData,
  useExplorationState,
  useFilteredVolumes,
} from '../../context/ExplorationContext';
import { volumeAccountKey } from '../../lib/grouping/groupingPathShared';
import { isVolumeManaged } from '../../lib/classification';
import { formatBytes, volumeDisplayName, volumeSubtitle } from '../../lib/formatBytes';
import SidebarClearSelectionButton from './SidebarClearSelectionButton';
import {
  autoscalingDetailLabel,
  autoscalingIconClass,
  eligibilityDetailLabel,
  eligibilityIndicatorClass,
} from './volumeDetailStatusStyles';
import type {
  VolumeDetailsProps,
  VolumeInfoCardProps,
  VolumeRowsProps,
  VolumeSizeCardProps,
  VolumeStatusItemProps,
  VolumeStatusProps,
  VolumeTagItemProps,
  VolumeTagsProps,
} from './volumeDetails.types';
import SavingOpportunityCard from './shared/SavingOpportunityCard';

function VolumeInfoCard({
  volume,
  originalSizeLabel,
  currentSizeLabel,
  changePercent,
  showRange,
}: VolumeInfoCardProps) {
  const title = volumeDisplayName(volume);

  return (
    <SidebarCard>
      <div className="mb-3.5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <Link
            to={`/fleet-manager/${volume.ebsId}`}
            className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded hover:opacity-80 focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-1"
            title={title}
            aria-label={`Open ${title} in Fleet Manager`}
          >
            <Typography
              variant="p2-semi"
              fontWeight={500}
              color="text-primary-900"
              className="font-family-inter min-w-0 flex-1 whitespace-nowrap text-ellipsis overflow-hidden !leading-[20px]"
            >
              {title}
            </Typography>
            <LinkIcon className="size-[15px] shrink-0 text-gray-600" aria-hidden />
          </Link>
        </div>
        <Typography variant="caption" color="text-gray-700" className="font-family-inter">
          {volumeSubtitle(volume)}
        </Typography>
      </div>
      <div className="pt-3 flex gap-3">
        {showRange ? (
          <>
            <VolumeSizeCard label="Original Size" value={originalSizeLabel} />
            <VectorIcon className="size-5 mt-auto mb-1 text-gray-600" aria-hidden="true" />
            <VolumeSizeCard
              label="Current Size"
              value={currentSizeLabel}
              changePercent={changePercent ?? undefined}
            />
          </>
        ) : (
          <VolumeSizeCard label="Size" value={originalSizeLabel} />
        )}
      </div>
    </SidebarCard>
  );
}

function VolumeSizeCard({ label, value, changePercent }: VolumeSizeCardProps) {
  return (
    <div className="pt-3">
      <Typography variant="p3" fontWeight={500} className="font-family-inter">
        {label}
      </Typography>
      <div className="flex items-center gap-3">
        <Typography variant="H3" fontWeight={600} className="mt-3.5 font-family-outfit">
          {value}
        </Typography>
        {changePercent && changePercent !== '0%' && (
          <StorageChangeTag changePercent={changePercent} className="mt-auto" />
        )}
      </div>
    </div>
  );
}

function VolumeStatus({ volume }: VolumeStatusProps) {
  const { apiVolumeByEbsId } = useExplorationData();

  return (
    <SidebarCard>
      <div className="grid grid-cols-2 gap-5">
        <VolumeStatusItem
          label="Eligibility"
          value={eligibilityDetailLabel(volume)}
          icon={
            <div
              className={clsx(
                'size-2 rounded-full mt-1.5 shrink-0',
                eligibilityIndicatorClass(volume)
              )}
              aria-hidden
            />
          }
        />
        <VolumeStatusItem
          label="Autoscaling"
          value={autoscalingDetailLabel(volume.activationStatus)}
          icon={
            <AutoscalingStatusIcon
              className={clsx('size-3.5 mt-1.5 shrink-0', autoscalingIconClass(volume))}
              aria-hidden
            />
          }
        />
      </div>
      <SavingOpportunityCard
        className="mt-5"
        volume={volume}
        fleetVolume={apiVolumeByEbsId[volume.ebsId]}
      />
    </SidebarCard>
  );
}

function VolumeStatusItem({ label, value, icon }: VolumeStatusItemProps) {
  return (
    <div>
      <Typography
        variant="caption"
        fontWeight={500}
        color="text-primary-900"
        className="font-family-inter"
      >
        {label}
      </Typography>
      <div className="flex items-center gap-1.5">
        {icon}
        <Typography
          variant="p2-semi"
          fontWeight={600}
          color="text-primary-900"
          className="font-family-outfit mt-1.5"
        >
          {value}
        </Typography>
      </div>
    </div>
  );
}

function VolumeRows({ label, item, tagVariant, onTagClick, tagAriaLabel }: VolumeRowsProps) {
  if (item == null || item === '') return null;

  const valueNode = !tagVariant ? (
    <Typography variant="p3" fontWeight={500} className="font-family-inter">
      {item}
    </Typography>
  ) : onTagClick ? (
    <Button
      type="button"
      variant="text"
      size="sm"
      className="!h-auto !p-0 !min-w-0 max-w-52"
      aria-label={tagAriaLabel}
      onClick={onTagClick}
    >
      <Tag className="max-w-52" variant={tagVariant} text={item.toString()} />
    </Button>
  ) : (
    <Tag className="max-w-52" variant={tagVariant} text={item.toString()} />
  );

  return (
    <div className="flex items-center justify-between px-5 py-2.5 h-12 border-b border-primary-70">
      <Typography variant="p3-reg" color="text-gray-700" className="font-family-inter">
        {label}
      </Typography>
      {valueNode}
    </div>
  );
}

function VolumeTags({ tags }: VolumeTagsProps) {
  const entries = Object.entries(tags);
  if (entries.length === 0) return null;

  return (
    <div className="px-5 py-2.5 border-b border-primary-70">
      <div className="flex items-center gap-2 py-2">
        <Typography variant="p3-reg" color="text-gray-700" className="font-family-inter">
          Tags
        </Typography>
        <span className="sr-only">{`${entries.length} tags`}</span>
        <Tag size="kpi" variant="kpi" text={String(entries.length)} className="max-w-52" />
      </div>
      <div className="flex flex-col gap-1.5">
        {entries.map(([name, value]) => (
          <VolumeTagItem key={name} name={name} value={value} />
        ))}
      </div>
    </div>
  );
}

function VolumeTagItem({ name, value }: VolumeTagItemProps) {
  return (
    <div className="flex flex-col gap-1.5 py-2">
      <Typography variant="p3-reg" color="text-gray-700" className="font-family-inter">
        {name}
      </Typography>
      <Tag variant="volume" text={value} showFullText truncateToContainer />
    </div>
  );
}

export default function VolumeDetails({ volume }: VolumeDetailsProps) {
  const actions = useExplorationActions();
  const state = useExplorationState();
  const filtered = useFilteredVolumes();

  const managed = isVolumeManaged(volume);
  const originalGiB = volume.size;
  const currentGiB = managed && volume.datafyDiskSize != null ? volume.datafyDiskSize : volume.size;
  const changePercent = formatStorageChangePercent(originalGiB, currentGiB);
  const showRange = managed && currentGiB !== originalGiB;

  const drillToCluster = () => {
    if (!volume.cluster) return;
    actions.setDrillPath({
      account: state.groupByAccount ? volumeAccountKey(volume) : undefined,
      structural: { kind: 'cluster', top: { kind: 'cluster', name: volume.cluster } },
    });
  };

  const drillToNodeGroup = () => {
    if (!volume.cluster || !volume.nodeGroup) return;
    actions.setDrillPath({
      account: state.groupByAccount ? volumeAccountKey(volume) : undefined,
      structural: {
        kind: 'cluster',
        top: { kind: 'cluster', name: volume.cluster },
        nodeGroup: volume.nodeGroup !== volume.cluster ? volume.nodeGroup : undefined,
      },
    });
  };

  const handleInstanceClick = () => {
    if (!volume.instanceId) return;
    const vols = filtered.filter((v) => v.instanceId === volume.instanceId);
    actions.selectInstance(volume.instanceId, vols);
  };

  const clusterClickable = state.secondary === 'cluster' && Boolean(volume.cluster);
  const nodeGroupClickable = state.secondary === 'cluster' && Boolean(volume.nodeGroup);

  return (
    <div>
      <VolumeInfoCard
        volume={volume}
        originalSizeLabel={formatBytes(originalGiB)}
        currentSizeLabel={formatBytes(currentGiB)}
        changePercent={changePercent}
        showRange={showRange}
        onClear={actions.clearSelection}
      />
      <VolumeStatus volume={volume} />
      <VolumeRows
        label="Cluster"
        item={volume.cluster}
        tagVariant="cluster"
        onTagClick={clusterClickable ? drillToCluster : undefined}
        tagAriaLabel={clusterClickable ? `Drill into cluster ${volume.cluster}` : undefined}
      />
      <VolumeRows
        label="Node group"
        item={volume.nodeGroup}
        tagVariant="node"
        onTagClick={nodeGroupClickable ? drillToNodeGroup : undefined}
        tagAriaLabel={nodeGroupClickable ? `Drill into node group ${volume.nodeGroup}` : undefined}
      />
      <VolumeRows
        label="Instance ID"
        item={volume.instanceId}
        tagVariant="instance"
        onTagClick={volume.instanceId ? handleInstanceClick : undefined}
        tagAriaLabel={volume.instanceId ? `View instance ${volume.instanceId} details` : undefined}
      />
      <VolumeRows label="IOPS" item={volume.iops != null ? volume.iops.toLocaleString() : null} />
      <VolumeRows
        label="Throughput"
        item={volume.throughput != null ? `${volume.throughput} MiB/s` : null}
      />
      <VolumeTags tags={volume.tags ?? {}} />
    </div>
  );
}
